import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { marketplaceApi } from "../api/client";
import MarketplaceHeader from "../components/MarketplaceHeader";
import ProductCard from "../components/ProductCard";
import TransporterServiceCard from "../components/TransporterServiceCard";
import FloatingTools from "../components/FloatingTools";
import Footer from "../components/Footer";

const defaultCategories = [
  { code: "ELECTRONICS", name: "Electronics" },
  { code: "FASHION", name: "Fashion" },
  { code: "GROCERIES", name: "Groceries" },
  { code: "HARDWARE", name: "Hardware & Tools" },
  { code: "AGRICULTURE", name: "Agriculture" },
  { code: "LOGISTICS", name: "Transport & Logistics" },
];

function categoryCode(category) {
  return category.code || category.id;
}

export default function PublicMarketplace() {
  const [marketplace, setMarketplace] = useState({ categories: [], products: [], homepage_sections: {}, transporter_services: [], sellers: [], stats: {}, API_online: false });
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("Products");
  const [loading, setLoading] = useState(true);
  const [feedError, setFeedError] = useState("");
  const navigate = useNavigate();

  async function loadMarketplace(params = {}) {
    setLoading(true);
    try {
      const res = await marketplaceApi.home(params);
      setMarketplace({ ...res.data, API_online: true });
      setFeedError("");
    } catch (err) {
      setMarketplace({ categories: defaultCategories, products: [], homepage_sections: {}, transporter_services: [], sellers: [], stats: {}, API_online: false });
      setFeedError("Marketplace listings could not be loaded right now. Please refresh shortly.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadMarketplace(); }, []);

  const rawCategories = marketplace.categories?.length ? marketplace.categories : defaultCategories;
  const categories = rawCategories.filter((category) => !category.parent && !category.parent_name);
  const parentCategories = categories.length ? categories : rawCategories;
  const products = marketplace.products || [];
  const sellers = marketplace.sellers || [];
  const transporterServices = marketplace.transporter_services || [];
  const sectionPayload = marketplace.homepage_sections || {};
  const productSections = [
    { key: "featured", title: "Featured picks", text: "Quality marketplace products selected for discovery.", items: sectionPayload.featured || [] },
    { key: "popular", title: "Popular products", text: "Products attracting orders and customer interest.", items: sectionPayload.popular || [] },
    { key: "new_arrivals", title: "New arrivals", text: "Recently published products from marketplace sellers.", items: sectionPayload.new_arrivals || [] },
    { key: "recommended", title: "Explore more", text: "A rotating mix across sellers and categories.", items: sectionPayload.recommended || [] },
  ];

  const filteredProducts = useMemo(() => {
    const clean = query.trim().toLowerCase();
    if (!clean) return products;
    return products.filter((item) => `${item.title} ${item.category_name || ""} ${item.seller_name || ""}`.toLowerCase().includes(clean));
  }, [query, products]);

  function runSearch(event) {
    event.preventDefault();
    const clean = query.trim();
    if (!clean) return;
    if (tab === "TAZY AI") {
      navigate(`/ai?q=${encodeURIComponent(clean)}`);
      return;
    }
    navigate(`/search?q=${encodeURIComponent(clean)}&tab=${encodeURIComponent(tab)}`);
  }

  const stats = marketplace.stats || {};
  const heroStats = [
    { label: "Live listings", value: stats.available_public_products || products.length || 0 },
    { label: "Categories", value: stats.categories || parentCategories.length || 0 },
    { label: "Sellers", value: sellers.length || stats.verified_sellers || 0 },
    { label: "Transporters", value: transporterServices.length || stats.transporters || 0 },
  ];

  return (
    <div className="page-shell marketplace-page" id="top">
      <MarketplaceHeader />

      <main>
        <section className="market-hero market-hero--banner">
          <div className="container marketplace-banner-panel">
            <div className="marketplace-banner-copy">
              <span className="eyebrow">TAZY CART MARKETPLACE</span>
              <h1>Search products, sellers and delivery services from one marketplace.</h1>
              <p>Buyers can browse live listings, send protected enquiries, place orders, source products and connect fulfilment through registered transporters.</p>

              <form className="search-panel search-panel--banner market-search-pro" onSubmit={runSearch}>
                <div className="search-mode-tabs market-search-pro__tabs" role="tablist" aria-label="Search mode">
                  {["Products", "Suppliers", "Transporters", "TAZY AI"].map((item) => (
                    <button type="button" key={item} className={tab === item ? "active" : ""} onClick={() => setTab(item)}>{item}</button>
                  ))}
                </div>
                <div className="search-line market-search-pro__line">
                  <span className="market-search-pro__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="7" />
                      <path d="M20 20l-3.5-3.5" />
                    </svg>
                  </span>
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={tab === "Suppliers" ? "Search verified sellers, shops or business categories" : tab === "Transporters" ? "Search transport routes, service areas or couriers" : tab === "TAZY AI" ? "Ask TAZY AI what you want to source" : "Search products, categories, brands or sellers"}
                  />
                  <button type="submit">{tab === "Suppliers" ? "Find sellers" : tab === "Transporters" ? "Find transporters" : tab === "TAZY AI" ? "Ask" : "Search"}</button>
                </div>
                <div className="market-search-pro__hint" aria-label="Search helper">
                  <span>{tab === "TAZY AI" ? "Ask for sourcing guidance, product ideas or supplier suggestions." : tab === "Suppliers" ? "Find verified sellers by shop name, category or location." : tab === "Transporters" ? "Find couriers by route, service area or delivery type." : "Search products by name, category, brand or seller."}</span>
                </div>
              </form>

              <div className="hero-action-row">
                <Link to="/request">Start sourcing request</Link>
                <Link to="/sellers">View verified sellers</Link>
                <Link to="/transporters">Explore transporters</Link>
              </div>
            </div>
            <div className="marketplace-banner-visual" aria-hidden="true">
              <img src="/images/tazy-marketplace-banner-new.webp" alt="" />
            </div>
          </div>
        </section>

        <section className="welcome-strip welcome-strip--interactive">
          <div className="container welcome-strip__inner">
            <strong>Welcome to TAZY CART</strong>
            <div>
              <Link to="/request">Request quotation</Link>
              <Link to="/sellers">Verified sellers</Link>
              <Link to="/transporters">Courier network</Link>
            </div>
          </div>
        </section>

        <section className="container marketplace-stat-row">
          {heroStats.map((item) => <div key={item.label}><strong>{item.value}</strong><span>{item.label}</span></div>)}
        </section>

        <section className="market-grid container market-grid--compact">
          <aside className="category-panel category-panel--balanced" id="categories">
            <div className="panel-title">Categories for you</div>
            <div className="category-scroll-list category-scroll-list--balanced">
              {parentCategories.map((category) => (
                <Link key={categoryCode(category)} to={`/categories/${categoryCode(category)}`} className="category-link-row">
                  <span>{category.name}</span>
                  <b>›</b>
                </Link>
              ))}
            </div>
          </aside>

          <section className="content-grid content-grid--compact marketplace-showcase-grid phase1o-market-grid">
            <div className="promo-card promo-card--quote showcase-card compact-showcase-card image-showcase-card source-card-v2 marketplace-action-card">
              <div className="showcase-copy"><span>RFQ</span><h2>Source what you need</h2><p>Send a buying request and let verified sellers confirm stock, price and delivery terms.</p><button type="button" onClick={() => navigate("/request")}>Start request</button></div>
              <div className="showcase-photo-frame source-photo-frame" aria-hidden="true"><img src="/images/tazy-source-goods.jpg" alt="" /></div>
            </div>
            <div className="promo-card promo-card--hot showcase-card showcase-products-card showcase-products-card-v2">
              <div className="showcase-card-head"><span>Hot picks</span><h2>Products moving now</h2></div>
              <div className="showcase-mini-products showcase-mini-products-v2">
                {(sectionPayload.popular?.length ? sectionPayload.popular : filteredProducts).slice(0, 4).map((item, index) => (
                  <ProductCard key={`showcase-${item.id || item.title}-${index}`} item={item} />
                ))}
              </div>
            </div>
            <div className="promo-card promo-card--transport showcase-card compact-showcase-card image-showcase-card transport-card-v2 marketplace-action-card" id="transporters">
              <div className="showcase-copy"><span>Logistics</span><h2>Choose a transporter</h2><p>Transporters showcase routes, service areas and delivery pricing so sellers and businesses can choose the right fulfilment partner.</p><Link to="/transporters">Explore couriers</Link></div>
              <div className="showcase-photo-frame transport-photo-frame" aria-hidden="true"><img src="/images/tazy-transport-truck.jpg" alt="" /></div>
            </div>
          </section>
        </section>



        <section className="container transporter-services-strip" id="transporters-list">
          <div className="section-heading">
            <div>
              <h2>Transporter services on TAZY CART</h2>
              <p>Registered transporters can market delivery services just like sellers market products.</p>
            </div>
            <Link to="/transporters">View all transporters</Link>
          </div>
          <div className="transport-services-market-grid transport-services-market-grid--home">
            {transporterServices.length ? transporterServices.slice(0, 4).map((service) => <TransporterServiceCard key={service.id} service={service} compact />) : <div className="marketplace-empty-feed">No approved transporter services yet. Courier accounts can add services from their transporter workspace.</div>}
          </div>
        </section>

        <div className="marketplace-product-rails" id="verified">
          {loading ? <div className="container marketplace-empty-feed">Loading marketplace listings...</div> : productSections.some((section) => section.items.length) ? productSections.map((section) => section.items.length ? (
            <section className="container products-section products-section--compact marketplace-product-rail" key={section.key}>
              <div className="section-heading">
                <div><h2>{section.title}</h2><p>{section.text}</p></div>
                <Link to="/search?tab=Products">View all products</Link>
              </div>
              <div className="product-grid product-grid--compact">
                {section.items.slice(0, 6).map((item) => <ProductCard key={`${section.key}-${item.id || item.title}`} item={item} />)}
              </div>
            </section>
          ) : null) : <div className="container marketplace-empty-feed">{feedError || "No public products are visible yet."}</div>}
        </div>

        <section className="business-band" id="business">
          <div className="container business-band__inner">
            <div>
              <span>Business operations</span>
              <h2>Built for sellers, bigger shops and transport partners.</h2>
              <p>TAZY CART connects sourcing, inventory activity, documents, subscriptions, commission workflows, protected communication and AI support in one marketplace engine.</p>
            </div>
            <div className="business-stats">
              <div><strong>{stats.categories || parentCategories.length}</strong><span>Categories</span></div>
              <div><strong>{stats.featured_products || products.length}</strong><span>Listings</span></div>
              <div><strong>{stats.subscription_plans || 4}</strong><span>Plans</span></div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingTools />
    </div>
  );
}
